/**
 * 🎁 Admin Referral Service
 * Handles admin endpoints for referral management
 */

import apiClient from './apiClient';
import { getApiBaseUrl } from '@/config/environment';



export interface AdminReferral {
  id: string;
  signupDate: string;
  status: 'active' | 'completed' | 'credited' | 'pending';
  codeUsed: string;

  referrer: {
    id: string;
    fullName: string;
    email: string;
    phone: string;
    code: string;
  };

  referred: {
    id: string;
    fullName: string;
    email: string;
    phone: string;
    isVerified: boolean;
    totalBookings: number;
    hasCompletedFirstBooking: boolean;
  };

  reward: {
    amount: number;
    status: 'pending' | 'credited';
    creditedAt: string | null;
  };

  firstBooking: {
    id: string;
    created_at: string;
    status: string;
    total_price: number;
  } | null;
}

export interface ReferralSummary {
  totalReferrals: number;
  activeReferrals: number;
  completedReferrals: number;
  creditedReferrals: number;
  pendingReferrals: number;
  totalRewardsPaid: number;
  totalPendingRewards: number;
}

export interface ReferralPagination {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
}

export interface ReferralSettings {
  referrerReward: number;
  refereeReward: number;
  minBookingsForCompletion: number;
  rewardExpiryDays: number;
  updatedAt?: string;
  updatedBy?: string;
}

export const adminReferralService = {
  /**
   * Get all referrals with pagination and filters
   */
  async getReferrals(params?: {
    page?: number;
    limit?: number;
    status?: 'active' | 'completed' | 'credited' | 'pending' | 'all';
    search?: string;
  }): Promise<{
    success: boolean;
    data?: {
      summary: ReferralSummary;
      referrals: AdminReferral[];
      pagination?: ReferralPagination;
      stats?: ReferralSummary; // For backward compatibility
    };
    error?: string;
  }> {
    try {
      const queryParams = new URLSearchParams();

      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.status && params.status !== 'all') queryParams.append('status', params.status);
      if (params?.search) queryParams.append('search', params.search);

      const url = `/api/v1/admin/referrals/list${queryParams.toString() ? `?${queryParams}` : ''}`;


      const response = await apiClient.get(url);


      if (response.data.success) {
        return {
          success: true,
          data: {
            summary: response.data.data.summary,
            referrals: response.data.data.referrals,
            pagination: response.data.data.pagination,
            stats: response.data.data.summary // For backward compatibility
          }
        };
      } else {
        return {
          success: false,
          error: response.data.error?.message || 'Failed to fetch referrals'
        };
      }
    } catch (error: any) {
      console.error('❌ Error fetching referrals:', error);
      let errorMessage = 'Failed to fetch referrals';

      if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  },

  /**
   * Get current referral settings
   */
  async getSettings(): Promise<{
    success: boolean;
    data?: ReferralSettings;
    error?: string;
  }> {
    try {

      const response = await fetch(`${getApiBaseUrl()}/api/admin/referrals/settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          console.error('❌ Error response from server:', errorData);

          // Handle different error formats
          if (typeof errorData.error === 'string') {
            errorMessage = errorData.error;
          } else if (typeof errorData.message === 'string') {
            errorMessage = errorData.message;
          } else if (errorData.error && typeof errorData.error === 'object') {
            errorMessage = JSON.stringify(errorData.error);
          } else if (errorData.message && typeof errorData.message === 'object') {
            errorMessage = JSON.stringify(errorData.message);
          }
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      // Transform snake_case to camelCase for compatibility
      const rawData = result.data;
      const transformedData: ReferralSettings = {
        referrerReward: parseFloat(rawData.referrerReward || rawData.referrer_reward || 0),
        refereeReward: parseFloat(rawData.refereeReward || rawData.referee_reward || 0),
        minBookingsForCompletion: rawData.minBookingsForCompletion || rawData.min_bookings_for_completion || 1,
        rewardExpiryDays: rawData.rewardExpiryDays || rawData.reward_expiry_days || 90,
        updatedAt: rawData.updatedAt || rawData.updated_at,
        updatedBy: rawData.updatedBy || rawData.updated_by
      };

      return {
        success: true,
        data: transformedData
      };
    } catch (error) {
      console.error('❌ Error fetching settings:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch settings';
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  /**
   * Update referral settings
   */
  async updateSettings(settings: Partial<ReferralSettings>): Promise<{
    success: boolean;
    message?: string;
    data?: ReferralSettings;
    error?: string;
  }> {
    try {

      const response = await fetch(`${getApiBaseUrl()}/api/admin/referrals/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          console.error('❌ Error response from server:', errorData);

          // Handle different error formats
          if (typeof errorData.error === 'string') {
            errorMessage = errorData.error;
          } else if (typeof errorData.message === 'string') {
            errorMessage = errorData.message;
          } else if (errorData.error && typeof errorData.error === 'object') {
            errorMessage = JSON.stringify(errorData.error);
          } else if (errorData.message && typeof errorData.message === 'object') {
            errorMessage = JSON.stringify(errorData.message);
          }
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      // Transform snake_case to camelCase for compatibility
      const rawData = result.data;
      const transformedData: ReferralSettings = {
        referrerReward: parseFloat(rawData.referrerReward || rawData.referrer_reward || 0),
        refereeReward: parseFloat(rawData.refereeReward || rawData.referee_reward || 0),
        minBookingsForCompletion: rawData.minBookingsForCompletion || rawData.min_bookings_for_completion || 1,
        rewardExpiryDays: rawData.rewardExpiryDays || rawData.reward_expiry_days || 90,
        updatedAt: rawData.updatedAt || rawData.updated_at,
        updatedBy: rawData.updatedBy || rawData.updated_by
      };

      return {
        success: true,
        message: result.message,
        data: transformedData
      };
    } catch (error) {
      console.error('❌ Error updating settings:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update settings';
      return {
        success: false,
        error: errorMessage
      };
    }
  }
};
